import React, { useEffect, useMemo, useState } from 'react'
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { confirmOrderProposal } from '../../services/apis/aiChat'
import { selectOrdersHistory } from '../../selectors/orders'
import { selectPrimaryTradingAccount } from '../../selectors/user'
import { addMissingOrders } from '../../reducer/orders.reducer'
import { fetchUnseenLegOrders } from './deferredLegOrders'
import { OrderStatus } from '../../proto/generated/order'
import { exchangeFromJSON } from '../../proto/generated/common'
import { getSecurityAsync, getSecurityKey } from '../../util/security'
import OrderStatusDisplay from '../orderStatus/orderStatusDisplay'
import AiCard, { AiCardAccent, AiCardDivider } from './cardKit/AiCard'
import BracketLadder from './BracketLadder'
import { buildBracketLadder } from './bracketLadderModel'
import DataRow from './cardKit/DataRow'
import OrderTrackerActions from './OrderTrackerActions'
import { trackerActionsForOrder, type TrackerAction } from './trackerActions'
import DetailsDisclosure from './cardKit/DetailsDisclosure'
import NoticeStrip, { type Notice } from './cardKit/NoticeStrip'
import PreviewHeader from './cardKit/PreviewHeader'
import { usePreviewExpiryCountdown } from './cardKit/usePreviewExpiryCountdown'
import {
    estimateOrderValueRupees,
    formatCancelOrderOneLiner,
    getModifyFieldChanges,
    parseLtpRupeesFromPreviewMessages,
    partitionPreviewMessages,
} from './orderPreviewDisplay'
import {
    LIVE_ORDER_ACKNOWLEDGEMENT,
    buildConfirmOrderProposalRequest,
    buildOrderPreviewCardModel,
    classifyOrderPreviewError,
    displayQuantityForPreview,
    findLinkedOmsOrder,
    formatOrderPriceRupees,
    formatOrderTypeLabel,
    formatPositionExitLine,
    formatBasketLegLine,
    formatExitAllLegLine,
    formatPositionProtectionLine,
    formatProductLabel,
    formatValidityLabel,
    hasDeterministicExecutionFailure,
    hasSubmissionInProgress,
    isLiveOrderPreview,
    isCancelOrderPreview,
    isModifyOrderPreview,
    isOmsOrderTerminalFailure,
    isOrderPreviewExpired,
    isPostSubmissionOrderPreview,
    mergeExecutionIntoPreview,
    resolveOrderPreviewWithOms,
    toLinkedOmsOrder,
    type OrderPreview,
    type OrderPreviewIssue,
} from './domains/tradingDomain'

type Props = {
    preview: OrderPreview
    onPreviewChange?: (preview: OrderPreview) => void
}

type Transition = 'confirm' | 'cancel' | null

const LOCAL_EXECUTION_STATUSES = new Set(['SUBMITTING', 'SUBMITTED', 'EXECUTION_FAILED'])

function issueText(issue: OrderPreviewIssue, status: string): string {
    if (issue === 'stale') {
        return 'This preview version is stale or conflicts with the latest server state.'
    }
    if (issue === 'expired') {
        return 'This preview has expired and cannot be confirmed or submitted.'
    }
    if (status.toUpperCase() === 'CONFIRMED') {
        return 'The preview is confirmed, but submission could not be completed. No retry was sent.'
    }
    return 'The preview action could not be completed. No order was resent.'
}

function trackingStatusLine(preview: OrderPreview, cardModel: ReturnType<typeof buildOrderPreviewCardModel>): string | null {
    if (cardModel.omsStatus === OrderStatus.EXECUTED) {
        return 'Filled'
    }
    if (cardModel.omsStatus === OrderStatus.PARTIALLY_FILLED && cardModel.filledQuantity != null) {
        return `${cardModel.filledQuantity} of ${displayQuantityForPreview(preview)} filled`
    }
    if (cardModel.omsStatus === OrderStatus.CANCELLED) {
        return 'Cancelled at broker'
    }
    if (cardModel.omsStatus === OrderStatus.TRIGGER_PENDING) {
        return 'Waiting for trigger'
    }
    if (cardModel.omsStatus === OrderStatus.OPEN || cardModel.omsStatus === OrderStatus.PENDING) {
        return 'Working at broker'
    }
    return null
}

function cardAccent(
    preview: OrderPreview,
    cardModel: ReturnType<typeof buildOrderPreviewCardModel>,
    live: boolean,
): AiCardAccent {
    if (cardModel.trackingOms) return 'neutral'
    if (isCancelOrderPreview(preview)) return 'cancel'
    if (isModifyOrderPreview(preview)) return 'modify'
    if (live) return 'live'
    return cardModel.side === 'BUY' ? 'buy' : 'sell'
}

export default function OrderPreviewCard({ preview: initialPreview, onPreviewChange }: Props) {
    const ordersHistory = useSelector(selectOrdersHistory)
    const primaryTradingAccount = useSelector(selectPrimaryTradingAccount)
    const dispatch = useDispatch()
    const linkedOmsOrders = useMemo(() => ordersHistory.map(toLinkedOmsOrder), [ordersHistory])
    const [localPreview, setLocalPreview] = useState(initialPreview)
    const [transition, setTransition] = useState<Transition>(null)
    const [issue, setIssue] = useState<OrderPreviewIssue | null>(null)

    const preview = useMemo(
        () => resolveOrderPreviewWithOms(localPreview, linkedOmsOrders),
        [localPreview, linkedOmsOrders],
    )

    const linkedOrder = useMemo(
        () => findLinkedOmsOrder(preview, linkedOmsOrders),
        [preview, linkedOmsOrders],
    )

    useEffect(() => {
        setLocalPreview((current) => {
            const incomingStatus = initialPreview.status.toUpperCase()
            const currentStatus = current.status.toUpperCase()
            if (incomingStatus === 'SUPERSEDED' || incomingStatus === 'CANCELLED') {
                return initialPreview
            }
            if (
                LOCAL_EXECUTION_STATUSES.has(currentStatus) &&
                current.proposalId === initialPreview.proposalId &&
                current.confirmationToken === initialPreview.confirmationToken
            ) {
                return current
            }
            return initialPreview
        })
        if (initialPreview.status.toUpperCase() === 'SUPERSEDED') {
            setIssue(null)
            setTransition(null)
        }
    }, [initialPreview])

    const live = isLiveOrderPreview(preview)
    const modify = isModifyOrderPreview(preview)
    const cancel = isCancelOrderPreview(preview)
    const expired = isOrderPreviewExpired(preview) && !isPostSubmissionOrderPreview(preview, linkedOrder)
    const blocked = issue === 'stale' || issue === 'expired'
    const cardModel = buildOrderPreviewCardModel(preview, blocked, linkedOrder)
    const displayQuantity = displayQuantityForPreview(preview)
    const canConfirm = cardModel.canConfirm && transition === null
    const canDismiss = cardModel.canConfirm && transition === null
    const inProgress = hasSubmissionInProgress(preview)
    const failed = hasDeterministicExecutionFailure(preview)
    const omsFailed = linkedOrder ? isOmsOrderTerminalFailure(linkedOrder) : false
    const countdown = usePreviewExpiryCountdown(preview.expiresAt, !cardModel.trackingOms && canConfirm)

    const ltpRupees = useMemo(
        () => parseLtpRupeesFromPreviewMessages(preview.messages),
        [preview.messages],
    )

    const instrumentSecurityKey = useMemo(
        () => getSecurityKey(exchangeFromJSON(preview.instrument.exchange), preview.instrument.code),
        [preview.instrument.exchange, preview.instrument.code],
    )
    const [instrumentDisplayName, setInstrumentDisplayName] = useState<string | null>(null)
    useEffect(() => {
        let cancelled = false
        setInstrumentDisplayName(null)
        if (instrumentSecurityKey) {
            getSecurityAsync(instrumentSecurityKey).then((security) => {
                if (!cancelled) setInstrumentDisplayName(security?.displayName ?? null)
            })
        }
        return () => {
            cancelled = true
        }
    }, [instrumentSecurityKey])
    const { warnings: messageWarnings, detailLines: messageDetails } = useMemo(
        () => partitionPreviewMessages(preview.messages),
        [preview.messages],
    )

    const notices = useMemo((): Notice[] => {
        const items: Notice[] = messageWarnings.map((text) => ({ text, severity: 'warning' as const }))
        if (preview.status.toUpperCase() === 'SUPERSEDED') {
            items.push({ text: 'A newer proposal replaced this one.', severity: 'info' })
        }
        if (inProgress) {
            items.push({ text: 'Submission in progress. Do not resend.', severity: 'info' })
        }
        if (failed) {
            items.push({
                text: preview.executionErrorMessage || 'Order submission failed.',
                severity: 'error',
            })
        }
        if (omsFailed) {
            items.push({
                text: cardModel.brokerMessage || 'The broker rejected or failed this order.',
                severity: 'error',
            })
        }
        if (issue) {
            items.push({ text: issueText(issue, preview.status), severity: issue === 'error' ? 'error' : 'warning' })
        }
        if (expired && preview.status.toUpperCase() !== 'CANCELLED') {
            items.push({ text: 'This preview has expired.', severity: 'warning' })
        }
        if (preview.status.toUpperCase() === 'CANCELLED') {
            items.push({
                text: cancel
                    ? 'Cancellation proposal dismissed. No broker order was cancelled.'
                    : 'Preview discarded. No order was sent.',
                severity: 'info',
            })
        }
        if (cardModel.reviewProtectiveStopHint && !cardModel.trackingOms) {
            items.push({ text: cardModel.reviewProtectiveStopHint, severity: 'info' })
        }
        return items
    }, [
        messageWarnings,
        preview,
        cancel,
        inProgress,
        failed,
        omsFailed,
        cardModel.brokerMessage,
        cardModel.reviewProtectiveStopHint,
        cardModel.trackingOms,
        issue,
        expired,
    ])

    const detailLines = useMemo(() => {
        const lines: { label: string; value: string }[] = [
            { label: 'Instrument', value: instrumentDisplayName ?? cardModel.instrumentCode },
            { label: 'Account', value: `${cardModel.accountLabel} · ${cardModel.accountMode}` },
        ]
        if (preview.contractLabel) {
            lines.push({ label: 'Contract', value: preview.contractLabel })
        }
        if (preview.product) {
            lines.push({ label: 'Product', value: formatProductLabel(preview.product) })
        }
        if (preview.validity) {
            lines.push({ label: 'Validity', value: formatValidityLabel(preview.validity) })
        }
        if (preview.lots != null && preview.lotSize != null) {
            lines.push({
                label: 'Size',
                value: `${preview.lots} lot${preview.lots === 1 ? '' : 's'} (${preview.quantity} qty)`,
            })
        }
        if (preview.icebergLegs != null && preview.icebergLegs > 1) {
            lines.push({ label: 'Iceberg', value: `${preview.icebergLegs} limit legs` })
        }
        if (preview.disclosedQuantity != null && preview.disclosedQuantity > 0) {
            lines.push({ label: 'Disclosed qty', value: `${preview.disclosedQuantity} of ${preview.quantity}` })
        }
        if (preview.requiredMarginRupees != null && preview.requiredMarginRupees > 0) {
            lines.push({
                label: 'Est. margin',
                value: formatOrderPriceRupees(preview.requiredMarginRupees),
            })
        }
        if (cardModel.targetOrderIdShort) {
            lines.push({ label: 'Order ID', value: cardModel.targetOrderIdShort })
        }
        if (cardModel.orderId) {
            lines.push({ label: 'Broker order', value: cardModel.orderId })
        }
        for (const extra of messageDetails) {
            lines.push({ label: 'Note', value: extra })
        }
        return lines
    }, [cardModel, messageDetails, instrumentDisplayName])

    const handleConfirm = async () => {
        setIssue(null)
        setTransition('confirm')
        const acknowledgement = live ? LIVE_ORDER_ACKNOWLEDGEMENT : undefined
        try {
            const execution = await confirmOrderProposal(buildConfirmOrderProposalRequest(localPreview, acknowledgement))
            const merged = mergeExecutionIntoPreview(localPreview, execution)
            setLocalPreview(merged)
            onPreviewChange?.(merged)
            // AI orders are always placed on the primary account (AiTradingAccountResolver).
            void fetchUnseenLegOrders(
                execution.legs,
                new Set(ordersHistory.map((order) => order.orderId)),
                primaryTradingAccount?.accountId,
            ).then((orders) => {
                if (orders.length) dispatch(addMissingOrders({ orders }))
            })
        } catch (error) {
            const nextIssue = classifyOrderPreviewError(error)
            setIssue(nextIssue)
            if (nextIssue === 'expired') {
                const expiredPreview = { ...localPreview, status: 'EXPIRED' }
                setLocalPreview(expiredPreview)
                onPreviewChange?.(expiredPreview)
            }
        } finally {
            setTransition(null)
        }
    }

    const handleCancel = () => {
        setIssue(null)
        setTransition('cancel')
        const cancelled = { ...localPreview, status: 'CANCELLED' }
        setLocalPreview(cancelled)
        onPreviewChange?.(cancelled)
        setTransition(null)
    }

    const side = cardModel.side.toUpperCase() as 'BUY' | 'SELL'
    const accent = cardAccent(preview, cardModel, live)
    const orderValue = estimateOrderValueRupees(preview, ltpRupees)

    const basketLegs = preview.basketLegs?.length ? preview.basketLegs : null
    const exitAllLegs = preview.exitAll?.legs?.length ? preview.exitAll.legs : null

    const headerTitle = useMemo(() => {
        if (cancel) {
            return `Cancel · ${preview.instrument.label}`
        }
        if (modify) {
            const leg = cardModel.modifyLegLabel ? ` · ${cardModel.modifyLegLabel}` : ''
            return `Modify · ${preview.instrument.label}${leg}`
        }
        if (cardModel.isProtectiveStopBracket) {
            return (
                <>
                    <Typography component="span" color={side === 'BUY' ? 'buy.main' : 'sell.main'} fontWeight={700}>
                        {side}
                    </Typography>
                    {`  ${displayQuantity} × ${preview.instrument.label}  · Bracket`}
                </>
            )
        }
        if (cardModel.trackingOms) {
            return (
                <>
                    <Typography component="span" color={side === 'BUY' ? 'buy.main' : 'sell.main'} fontWeight={700}>
                        {side}
                    </Typography>
                    {`  ${displayQuantity} × ${preview.instrument.label}`}
                </>
            )
        }
        return (
            <>
                <Typography component="span" color={side === 'BUY' ? 'buy.main' : 'sell.main'} fontWeight={700}>
                    {side}
                </Typography>
                {`  ${displayQuantity} × ${preview.instrument.label}${basketLegs ? '  · Basket' : ''}${exitAllLegs ? '  · Exit all' : ''}${preview.condition ? '  · Conditional' : ''}${
                    preview.positionExit ? '  · Exit' : ''
                }${
                    preview.positionProtection ? '  · Stop / target' : ''
                }`}
            </>
        )
    }, [cancel, modify, cardModel, preview.instrument.label, preview.condition, preview.positionExit, preview.positionProtection, basketLegs, exitAllLegs, displayQuantity, side])

    const bracketLegs = useMemo(() => {
        if (!cardModel.isProtectiveStopBracket || !preview.legs?.length) return null
        const entry = preview.legs.find((leg) => leg.role === 'ENTRY')
        const stop = preview.legs.find((leg) => leg.role === 'PROTECTIVE_STOP')
        const target = preview.legs.find((leg) => leg.role === 'TARGET')
        return entry && stop ? { entry, stop, target } : null
    }, [cardModel.isProtectiveStopBracket, preview.legs])

    const ladder = useMemo(() => {
        if (!bracketLegs) return null
        const { entry, stop, target } = bracketLegs
        return buildBracketLadder({
            side: preview.side,
            quantity: preview.quantity,
            entryLimitPrice: entry.limitPrice ?? null,
            entryIsMarket: formatOrderTypeLabel(entry.orderType).toUpperCase() === 'MARKET',
            stopPrice: stop.triggerPrice ?? stop.limitPrice ?? null,
            targetPrice: target ? (target.limitPrice ?? target.triggerPrice ?? null) : null,
            ltpRupees,
        })
    }, [bracketLegs, preview.side, preview.quantity, ltpRupees])

    /** Post-confirmation OMS state per bracket leg, matched by the compiler's stable client order ids. */
    const bracketLegOms = useMemo(() => {
        if (!bracketLegs || !cardModel.trackingOms) return null
        const find = (clientOrderId: string) => linkedOmsOrders.find((order) => order.clientOrderId === clientOrderId)
        return {
            ENTRY: find(bracketLegs.entry.clientOrderId),
            STOP: find(bracketLegs.stop.clientOrderId),
            TARGET: bracketLegs.target ? find(bracketLegs.target.clientOrderId) : undefined,
        }
    }, [bracketLegs, cardModel.trackingOms, linkedOmsOrders])

    const trackerActions = useMemo((): TrackerAction[] => {
        if (!cardModel.trackingOms || cancel || modify) return []
        if (bracketLegs && bracketLegOms) {
            return [
                ...trackerActionsForOrder({
                    orderId: bracketLegOms.STOP?.orderId,
                    status: bracketLegOms.STOP?.status,
                    orderType: bracketLegs.stop.orderType,
                    roleLabel: 'stop',
                }),
                ...(bracketLegs.target
                    ? trackerActionsForOrder({
                          orderId: bracketLegOms.TARGET?.orderId,
                          status: bracketLegOms.TARGET?.status,
                          orderType: bracketLegs.target.orderType,
                          roleLabel: 'target',
                      })
                    : []),
            ]
        }
        return trackerActionsForOrder({
            orderId: linkedOrder?.orderId ?? preview.orderId,
            status: linkedOrder?.status,
            orderType: preview.orderType,
            conditional: preview.condition != null,
        })
    }, [cardModel.trackingOms, cancel, modify, bracketLegs, bracketLegOms, linkedOrder, preview])

    const body = useMemo(() => {
        if (cardModel.trackingOms && ladder && bracketLegOms) {
            const statusLine = trackingStatusLine(preview, cardModel)
            return (
                <Stack spacing={0.75}>
                    {statusLine ? (
                        <Typography variant="body2" fontWeight={600}>
                            {statusLine}
                        </Typography>
                    ) : null}
                    <BracketLadder
                        ladder={ladder}
                        legStatuses={{
                            ENTRY: { status: bracketLegOms.ENTRY?.status, filledQuantity: bracketLegOms.ENTRY?.filledQuantity, quantity: bracketLegs?.entry.quantity },
                            STOP: { status: bracketLegOms.STOP?.status, filledQuantity: bracketLegOms.STOP?.filledQuantity, quantity: bracketLegs?.stop.quantity },
                            TARGET: { status: bracketLegOms.TARGET?.status, filledQuantity: bracketLegOms.TARGET?.filledQuantity, quantity: bracketLegs?.target?.quantity },
                        }}
                    />
                </Stack>
            )
        }
        if (cardModel.trackingOms) {
            const statusLine = trackingStatusLine(preview, cardModel)
            return (
                <Stack spacing={0.75} className="font-feature-num">
                    {statusLine ? (
                        <Typography variant="body2" fontWeight={600}>
                            {statusLine}
                        </Typography>
                    ) : null}
                    {cardModel.omsStatus !== undefined ? (
                        <Typography variant="caption" color="text.secondary">
                            <OrderStatusDisplay status={cardModel.omsStatus} />
                        </Typography>
                    ) : null}
                </Stack>
            )
        }

        if (cancel && cardModel.cancelBracketLegs?.length) {
            return (
                <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                        {cardModel.cancelBracketLegs.filter((leg) => leg.willCancel).length} leg(s) will be cancelled
                    </Typography>
                    {cardModel.cancelBracketLegs.map((leg) => (
                        <Typography
                            key={leg.title}
                            variant="caption"
                            color={leg.willCancel ? 'text.primary' : 'text.disabled'}
                            display="block"
                        >
                            {leg.title}: {leg.side} · {leg.orderTypeLabel}
                            {leg.willCancel ? '' : ' · already complete'}
                        </Typography>
                    ))}
                </Stack>
            )
        }

        if (cancel && cardModel.cancelOrderDetails) {
            const line = formatCancelOrderOneLiner(
                cardModel.side,
                cardModel.cancelOrderDetails.quantity,
                cardModel.cancelOrderDetails.orderTypeLabel,
                cardModel.cancelOrderDetails.limitPriceLabel,
                cardModel.cancelOrderDetails.triggerPriceLabel,
                cardModel.currentSnapshotStatus,
            )
            return (
                <Typography variant="body2" fontWeight={600} className="font-feature-num">
                    {line}
                </Typography>
            )
        }

        if (modify && cardModel.modifyCurrent && cardModel.modifyProposed) {
            const changes = getModifyFieldChanges(cardModel.modifyCurrent, cardModel.modifyProposed)
            if (!changes.length) {
                return (
                    <Typography variant="body2" color="text.secondary">
                        No field changes detected.
                    </Typography>
                )
            }
            return (
                <Stack spacing={0.75}>
                    {changes.map((change) => (
                        <Typography key={change.label} variant="body2" className="font-feature-num">
                            {change.label}{' '}
                            <Typography component="span" color="text.secondary">
                                {change.fromLabel}
                            </Typography>
                            {' → '}
                            <Typography component="span" fontWeight={700}>
                                {change.toLabel}
                            </Typography>
                        </Typography>
                    ))}
                </Stack>
            )
        }

        if (ladder) {
            return <BracketLadder ladder={ladder} />
        }

        const typeKey = preview.orderType.trim().toUpperCase().replace(/-/g, '_')
        const typeLabel = formatOrderTypeLabel(preview.orderType)
        let priceLine: string | null = null
        if (typeKey === 'LIMIT' || typeKey === 'SL') {
            const limit = cardModel.limitPriceLabel ?? (preview.limitPrice ? formatOrderPriceRupees(preview.limitPrice) : null)
            const ltpPart = ltpRupees != null ? `          LTP ${formatOrderPriceRupees(ltpRupees)}` : ''
            priceLine = limit ? `${typeLabel}  ${limit}${ltpPart}` : typeLabel
        } else if (typeKey === 'SL_M') {
            const trigger =
                cardModel.triggerPriceLabel ??
                (preview.triggerPrice ? formatOrderPriceRupees(preview.triggerPrice) : null)
            const ltpPart = ltpRupees != null ? `          LTP ${formatOrderPriceRupees(ltpRupees)}` : ''
            priceLine = trigger ? `Trigger  ${trigger}${ltpPart}` : typeLabel
        } else {
            const ltpPart = ltpRupees != null ? `          LTP ${formatOrderPriceRupees(ltpRupees)}` : ''
            priceLine = `${typeLabel}${ltpPart}`
        }

        return (
            <Stack spacing={0.5}>
                <Typography variant="body2" fontWeight={600} className="font-feature-num">
                    {priceLine}
                </Typography>
                {orderValue != null ? (
                    <Typography variant="subtitle2" fontWeight={700} className="font-feature-num">
                        {formatOrderPriceRupees(orderValue)}
                    </Typography>
                ) : null}
            </Stack>
        )
    }, [cardModel, preview, ltpRupees, orderValue, cancel, modify, ladder, bracketLegOms, bracketLegs])

    const showActions = canConfirm || canDismiss || transition

    return (
        <AiCard
            accent={accent}
            actions={
                showActions ? (
                    <>
                        <Button
                            size="small"
                            variant="contained"
                            color={side === 'BUY' ? 'buy' : 'sell'}
                            onClick={() => void handleConfirm()}
                            disabled={!canConfirm}
                        >
                            {cardModel.confirmButtonLabel}
                        </Button>
                        <Button size="small" color="inherit" onClick={() => void handleCancel()} disabled={!canDismiss}>
                            {cancel ? 'Dismiss' : 'Discard'}
                        </Button>
                        {!cardModel.trackingOms && countdown ? (
                            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }} className="font-feature-num">
                                {countdown}
                            </Typography>
                        ) : null}
                        {transition ? (
                            <Stack direction="row" spacing={0.75} alignItems="center">
                                <CircularProgress size={14} />
                                <Typography variant="caption" color="text.secondary">
                                    Submitting…
                                </Typography>
                            </Stack>
                        ) : null}
                    </>
                ) : undefined
            }
        >
            <PreviewHeader title={headerTitle} live={live} />
            <AiCardDivider />
            <Box>{body}</Box>
            {preview.positionProtection && !cancel && !modify ? (
                <Box sx={{ mt: 1 }}>
                    <DataRow label="Protects" value={formatPositionProtectionLine(preview.positionProtection)} mono />
                </Box>
            ) : null}
            {preview.positionExit && !cancel && !modify ? (
                <Box sx={{ mt: 1 }}>
                    <DataRow label="Closes position" value={formatPositionExitLine(preview.positionExit)} mono />
                </Box>
            ) : null}
            {preview.positionProtection?.condition && !cancel && !modify ? (
                <Box sx={{ mt: 1 }}>
                    <DataRow label="Exit when" value={preview.positionProtection.condition.summary} mono />
                </Box>
            ) : null}
            {preview.condition && !cancel && !modify ? (
                <Box sx={{ mt: 1 }}>
                    <DataRow label="Condition" value={preview.condition.summary} mono />
                </Box>
            ) : null}
            {exitAllLegs && !cancel && !modify
                ? exitAllLegs.map((leg, index) => (
                      <Box key={leg.clientOrderId} sx={{ mt: 1 }}>
                          <DataRow
                              label={index === 0 ? 'Closing' : ''}
                              value={formatExitAllLegLine(leg)}
                              mono
                          />
                      </Box>
                  ))
                : null}
            {basketLegs && !cancel && !modify
                ? basketLegs.map((leg, index) => (
                      <Box key={leg.clientOrderId} sx={{ mt: 1 }}>
                          <DataRow label={index === 0 ? 'Legs' : ''} value={formatBasketLegLine(leg)} mono />
                      </Box>
                  ))
                : null}
            <OrderTrackerActions actions={trackerActions} />
            {!cardModel.trackingOms ? <DetailsDisclosure lines={detailLines} /> : null}
            <NoticeStrip notices={notices} />
        </AiCard>
    )
}
